import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { api, ApiError, type InboxRow } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { EmojiPicker } from './EmojiPicker.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';
import { UtilityFallback } from './UtilityFallback.js';

/**
 * Facebook's own six, for reacting to a message. Not the general emoji set:
 * Meta's Like edge on a comment has no reaction to choose at all — this row
 * only ever reacts to a Messenger or WhatsApp message, where any emoji is
 * accepted, and these are the ones a person expects to find first.
 */
const MESSAGE_REACTIONS = ['👍', '❤️', '😆', '😮', '😢', '😡'];

/**
 * Like a comment, or open a small menu to react to a message.
 *
 * Two different Graph calls behind one button, same as the reply endpoint
 * already branches by channel. There is no way to ask Meta whether this item
 * already carries our reaction, so the button always places one rather than
 * pretending to track state it cannot see — "remove reaction" sits beside it
 * for the other half.
 */
function ReactionButton({ item }: { item: InboxRow }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const react = useMutation({
    mutationFn: (emoji?: string) => api.reactToInboxItem(item.id, emoji),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
  const unreact = useMutation({
    mutationFn: () => api.removeInboxReaction(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  const isComment = item.channel === 'PAGE_COMMENT';

  return (
    <>
      <div className="emoji-picker" ref={container}>
        <button
          type="button"
          className="ghost small"
          disabled={react.isPending}
          onClick={() => (isComment ? react.mutate(undefined) : setOpen((current) => !current))}
          title={isComment ? t('inbox.like') : t('inbox.react')}
        >
          👍 {isComment ? t('inbox.like') : t('inbox.react')}
        </button>

        {!isComment && open && (
          <div className="emoji-picker-panel" role="menu">
            {MESSAGE_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="emoji-picker-option"
                onClick={() => react.mutate(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className="ghost small"
        disabled={unreact.isPending}
        onClick={() => unreact.mutate()}
        title={t('inbox.removeReaction')}
      >
        {t('inbox.removeReaction')}
      </button>

      {(react.isError || unreact.isError) && (
        <ErrorNotice error={(react.error ?? unreact.error) as Error} />
      )}
    </>
  );
}

/**
 * One waiting item: who said it, under which post, and what to do about it.
 *
 * Shared by the inbox and the comment browser deliberately. The two screens ask
 * different questions — "what is left" and "what was said on this Page" — but
 * an item is an item, and two copies of this would drift the way ErrorNotice
 * did until one of them stopped showing the reason a request failed.
 */
export function InboxCard({
  item,
  locale,
  readOnly,
  onOpenConversation,
}: {
  item: InboxRow;
  locale: string;
  readOnly: boolean;
  /**
   * Open the whole exchange this row belongs to.
   *
   * Optional because this card is also used where there is nowhere to go —
   * the Page's own comment history, which is a record and not a queue.
   */
  onOpenConversation?: (conversationId: string) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  /** So the emoji picker knows where the cursor is, and can hand focus back. */
  const draftInput = useRef<HTMLInputElement>(null);
  /*
   * Which saved reply the draft came from, if any.
   *
   * Tracked rather than matched on text afterwards, because the operator may
   * edit the wording before sending — a saved reply used as a starting point is
   * still a saved reply used, and matching on the final text would miss it.
   * Cleared the moment they type, since by then the text is theirs.
   */
  const [fromSaved, setFromSaved] = useState<string | null>(null);
  /*
   * The agent draft this reply started from, kept **through** editing.
   *
   * The mirror image of `fromSaved` below: a canned sentence stops being that
   * sentence once it is reworded, but a draft that is reworded is still the
   * draft — reworded, which is the whole point of recording it.
   */
  const [fromSuggestion, setFromSuggestion] = useState<string | null>(null);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const reply = useMutation({
    mutationFn: () =>
      api.replyToInboxItem(item.id, draft, {
        savedReplyId: fromSaved ?? undefined,
        fromSuggestionId: fromSuggestion ?? undefined,
      }),
    onSuccess: () => {
      setFromSaved(null);
      done();
    },
  });

  /*
   * Loaded once for the whole panel rather than per row: React Query dedupes
   * the key, so twenty cards on screen make one request. Left stale for a
   * while too — the list changes when somebody edits it, not while an inbox is
   * being worked through.
   */
  const saved = useQuery({
    queryKey: ['saved-replies'],
    queryFn: api.savedReplies,
    staleTime: 5 * 60_000,
  });
  const dismiss = useMutation({
    mutationFn: () => api.dismissInboxItem(item.id),
    onSuccess: done,
  });

  /*
   * Asking again adds a draft rather than replacing one. The whole point of a
   * second opinion is having the first still there to compare it with.
   */
  const suggest = useMutation({
    mutationFn: () => api.suggestInboxReply(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  return (
    <li className="inbox-item">
      <div className="inbox-meta">
        {/* Which channel, said in a word. A private message and a public
            comment read the same in a list and are not the same thing to
            answer — one is seen by everyone, the other by one person. */}
        <span className={`chan chan-${item.channel.toLowerCase()}`}>
          {t(`inbox.channel.${item.channel}` as never)}
        </span>
        <strong>{item.pageName}</strong>
        {item.postExcerpt && <span className="muted small">· {item.postExcerpt.slice(0, 48)}</span>}
        {item.handledExternally && (
          <span className="badge tiny">{t('inbox.handledElsewhere')}</span>
        )}
        <span className="muted small inbox-when">
          {new Date(item.createdAt).toLocaleString(locale)}
        </span>
      </div>

      {/*
        The message is the way into the conversation.

        A row here is one thing somebody said, and the question it usually
        raises — "what else has this person told us?" — was answerable only by
        going to another screen and finding them again. Pressing the message
        opens the exchange it came from.

        Still a paragraph when there is nowhere to go: a row from before
        conversations existed has no exchange to open, and a button that does
        nothing is worse than plain text.
      */}
      {onOpenConversation && item.conversationId ? (
        <button
          type="button"
          className="inbox-message inbox-message-link"
          onClick={() => onOpenConversation(item.conversationId as string)}
          title={t('inbox.openConversation')}
        >
          <span className="inbox-author">{item.authorName ?? t('inbox.someone')}:</span>{' '}
          {item.message ?? t('common.noText')}
        </button>
      ) : (
        <p className="inbox-message">
          {/* Meta withholds the author for anyone who has not authorised this
              app, which is nearly everyone. "Someone" is honest; a made-up name
              is not. */}
          <span className="inbox-author">{item.authorName ?? t('inbox.someone')}:</span>{' '}
          {item.message ?? t('common.noText')}
        </p>
      )}

      {!readOnly && (
        <>
          {item.suggestions.length > 0 && (
            <ul className="suggestions">
              {item.suggestions.map((suggestion) => (
                <li key={suggestion.id}>
                  <Icon.robot />
                  {/* Named, because two drafts on one row are trusted
                      differently: one is the Page's own assistant, the other is
                      software somebody connected. Choosing blind between them
                      is what this row used to ask of a person. */}
                  <span className="badge tiny">
                    {suggestion.source === 'agent'
                      ? t('inbox.fromAgent', { name: suggestion.sourceName ?? '' })
                      : t('inbox.fromAssistant', { name: suggestion.sourceName ?? '' })}
                  </span>
                  {suggestion.reply ? (
                    <>
                      <p>{suggestion.reply}</p>
                      {/* Puts the draft in the box rather than sending it. The
                          agent proposes; a person still presses send. */}
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => {
                          setDraft(suggestion.reply as string);
                          setFromSuggestion(suggestion.id);
                          setFromSaved(null);
                        }}
                      >
                        {t('inbox.useDraft')}
                      </button>
                    </>
                  ) : (
                    /* A refusal is shown, not hidden. "The agent would not
                       answer this" is information; an empty box is not. */
                    <p className="muted">
                      {/* The same distinction the conversation screen makes:
                          an assistant that would not answer is a judgement, and
                          one that could not be reached is a fault. Only the
                          live press carries a reason; a stored row has the
                          outcome and nothing more. */}
                      {suggestion.error
                        ? t('inbox.unreachable', { reason: suggestion.error })
                        : (suggestion.declineReason ??
                          t('inbox.declined', { outcome: suggestion.outcome }))}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/*
            The sentences this tenant sends every day, one click from the box.

            Chips rather than a dropdown: the whole value is that the answer is
            already decided, and a menu that has to be opened and read puts the
            decision back. Capped at six on screen — beyond that it stops being
            "the ones we always send".
          */}
          {(saved.data?.replies.length ?? 0) > 0 && (
            <div className="saved-replies">
              {saved.data?.replies.slice(0, 6).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="ghost small"
                  title={entry.body}
                  onClick={() => {
                    setDraft(entry.body);
                    setFromSaved(entry.id);
                    // A saved reply replaces the draft; it did not come from it.
                    setFromSuggestion(null);
                  }}
                >
                  {entry.title}
                </button>
              ))}
            </div>
          )}

          <div className="inbox-reply">
            <input
              ref={draftInput}
              value={draft}
              placeholder={t('inbox.replyPlaceholder')}
              onChange={(event) => {
                setDraft(event.target.value);
                // Once they have typed, the words are theirs.
                setFromSaved(null);
                // The draft's id survives it, deliberately: typing over an
                // agent's words is a correction of them, not a new answer.
                // Except when nothing is left — an emptied box has no origin.
                if (event.target.value.trim().length === 0) setFromSuggestion(null);
              }}
              onKeyDown={(event) => {
                // Enter sends, because the rhythm here is answer-and-next.
                if (event.key === 'Enter' && draft.trim() && !reply.isPending) reply.mutate();
              }}
            />
            <EmojiPicker target={draftInput} value={draft} onChange={setDraft} />
            <button
              type="button"
              className="primary"
              onClick={() => reply.mutate()}
              disabled={!draft.trim() || reply.isPending}
            >
              <Icon.send />
              {reply.isPending ? t('inbox.sending') : t('inbox.send')}
            </button>
            <button
              type="button"
              className="ghost small"
              onClick={() => suggest.mutate()}
              disabled={suggest.isPending}
              title={t('inbox.askAgentHint')}
            >
              <Icon.robot className={suggest.isPending ? 'spin' : undefined} />
              {suggest.isPending
                ? t('inbox.asking')
                : item.suggestions.length > 0
                  ? t('inbox.askAgain')
                  : t('inbox.askAgent')}
            </button>
            <ReactionButton item={item} />
            <button
              type="button"
              className="ghost small"
              onClick={() => dismiss.mutate()}
              disabled={dismiss.isPending}
            >
              {t('inbox.dismiss')}
            </button>
          </div>

          {/*
           * A closed 24-hour window is the one send failure with a legitimate
           * next step, so it opens that step instead of just reporting itself.
           * Every other error stays an error.
           */}
          {reply.isError &&
            (reply.error instanceof ApiError && reply.error.windowClosed ? (
              <UtilityFallback
                itemId={item.id}
                pageId={item.pageId}
                onSent={() => {
                  setDraft('');
                  reply.reset();
                }}
              />
            ) : (
              <ErrorNotice error={reply.error} />
            ))}
          {suggest.isError && <ErrorNotice error={suggest.error} />}
        </>
      )}
    </li>
  );
}
